import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { forumApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import RichContentEditor from '../../components/RichContentEditor';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, ArrowUp, ArrowDown, Pin, Lock, Trash2, Pencil, MessageCircle,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

const MOD_ROLES = ['super_admin', 'practice_admin'];

function VoteWidget({ score, yourVote, onVote, testidPrefix }) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-[36px]">
      <button onClick={() => onVote(yourVote === 1 ? 0 : 1)} className={`p-0.5 rounded ${yourVote === 1 ? 'text-[#0E7490]' : 'text-[#94A3B8] hover:text-[#0A2342]'}`} data-testid={`${testidPrefix}-up`}>
        <ArrowUp className="w-4 h-4" />
      </button>
      <div className="text-sm font-semibold text-[#0A2342]" data-testid={`${testidPrefix}-score`}>{score}</div>
      <button onClick={() => onVote(yourVote === -1 ? 0 : -1)} className={`p-0.5 rounded ${yourVote === -1 ? 'text-[#991B1B]' : 'text-[#94A3B8] hover:text-[#0A2342]'}`} data-testid={`${testidPrefix}-down`}>
        <ArrowDown className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ForumThreadPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingPost, setEditingPost] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await forumApi.getThread(threadId); setThread(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [threadId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /></div>;
  if (!thread) return <div className="p-8 text-center text-[#64748B]">Thread not found.</div>;

  const isMod = MOD_ROLES.includes(user?.role);

  const submitReply = async () => {
    if (!reply.trim()) return;
    setPosting(true);
    try {
      await forumApi.createPost({ thread_id: threadId, body_html: reply });
      setReply('');
      await load();
      toast.success('Reply posted');
    } catch { toast.error('Could not post'); }
    finally { setPosting(false); }
  };

  const voteThread = async (v) => {
    try {
      await forumApi.voteThread(threadId, v);
      await load();
    } catch { toast.error('Vote failed'); }
  };
  const votePost = async (postId, v) => {
    try {
      await forumApi.votePost(postId, v);
      await load();
    } catch { toast.error('Vote failed'); }
  };

  const togglePin = async () => {
    await forumApi.updateThread(threadId, { pinned: !thread.pinned });
    await load();
  };
  const toggleLock = async () => {
    await forumApi.updateThread(threadId, { locked: !thread.locked });
    await load();
  };
  const deleteThread = async () => {
    if (!window.confirm('Delete this thread?')) return;
    await forumApi.deleteThread(threadId);
    toast.success('Thread deleted');
    navigate(`/forum/c/${thread.category_id}`);
  };
  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    await forumApi.deletePost(postId);
    await load();
  };
  const saveEditedPost = async () => {
    await forumApi.updatePost(editingPost.id, editingPost.body_html);
    toast.success('Saved');
    setEditingPost(null);
    await load();
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="forum-thread-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(`/forum/c/${thread.category_id}`)} data-testid="back-to-cat-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to category</Button>

      <Card className="careiq-card">
        <CardContent className="p-4 flex gap-4">
          <VoteWidget score={thread.score} yourVote={thread.your_vote} onVote={voteThread} testidPrefix="thread-vote" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {thread.pinned && <Pin className="w-4 h-4 text-[#CA8A04]" />}
              {thread.locked && <Lock className="w-4 h-4 text-[#991B1B]" />}
              <h1 className="text-2xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="thread-title">{thread.title}</h1>
            </div>
            <div className="text-xs text-[#64748B] flex items-center gap-2 mb-3 flex-wrap">
              <span>{thread.author_name}</span>
              <Badge className="bg-[#F1F5F9] text-[#64748B] text-[10px]">{thread.author_role}</Badge>
              <span>·</span>
              <span>{new Date(thread.created_at).toLocaleString()}</span>
              <span>·</span>
              <span>{thread.view_count} views</span>
            </div>
            <div className="prose prose-sm max-w-none [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-lg [&_img]:rounded-lg" dangerouslySetInnerHTML={{ __html: thread.body_html || '<em class="text-[#94A3B8]">No body.</em>' }} data-testid="thread-body" />
            {(isMod || thread.author_id === user?.id) && (
              <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                {isMod && (
                  <>
                    <Button size="sm" variant="outline" onClick={togglePin} data-testid="thread-pin-btn"><Pin className="w-3 h-3 mr-1" />{thread.pinned ? 'Unpin' : 'Pin'}</Button>
                    <Button size="sm" variant="outline" onClick={toggleLock} data-testid="thread-lock-btn"><Lock className="w-3 h-3 mr-1" />{thread.locked ? 'Unlock' : 'Lock'}</Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={deleteThread} className="text-[#991B1B]" data-testid="thread-delete-btn"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="text-sm font-semibold text-[#64748B] flex items-center gap-2"><MessageCircle className="w-4 h-4" />{thread.posts.length} {thread.posts.length === 1 ? 'reply' : 'replies'}</div>

      <div className="space-y-2">
        {thread.posts.map((p) => (
          <Card key={p.id} className="careiq-card" data-testid={`forum-post-${p.id}`}>
            <CardContent className="p-4 flex gap-4">
              <VoteWidget score={p.score} yourVote={p.your_vote} onVote={(v) => votePost(p.id, v)} testidPrefix={`post-vote-${p.id}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#64748B] flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[#0A2342]">{p.author_name}</span>
                  <Badge className="bg-[#F1F5F9] text-[#64748B] text-[10px]">{p.author_role}</Badge>
                  <span>·</span>
                  <span>{new Date(p.created_at).toLocaleString()}</span>
                  {p.edited_at && <span className="italic">(edited)</span>}
                </div>
                <div className="prose prose-sm max-w-none mt-2 [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-lg [&_img]:rounded-lg" dangerouslySetInnerHTML={{ __html: p.body_html }} />
                {(isMod || p.author_id === user?.id) && (
                  <div className="flex gap-1 mt-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingPost(p)} data-testid={`edit-post-${p.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#991B1B]" onClick={() => deletePost(p.id)} data-testid={`delete-post-${p.id}`}><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!thread.locked ? (
        <Card className="careiq-card">
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold text-[#0A2342]">Add a reply</div>
            <RichContentEditor value={reply} onChange={setReply} placeholder="Write your reply…" minHeight={140} />
            <div className="flex justify-end">
              <Button className="careiq-btn-primary" onClick={submitReply} disabled={posting || !reply.replace(/<[^>]*>/g, '').trim()} data-testid="reply-post-btn">
                {posting && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Post reply
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="careiq-card border-l-4 border-l-[#991B1B]">
          <CardContent className="p-4 text-sm text-[#991B1B] flex items-center gap-2"><Lock className="w-4 h-4" />Thread is locked. Replies are disabled.</CardContent>
        </Card>
      )}

      <Dialog open={!!editingPost} onOpenChange={(v) => { if (!v) setEditingPost(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Edit post</DialogTitle></DialogHeader>
          {editingPost && <RichContentEditor value={editingPost.body_html} onChange={(v) => setEditingPost({ ...editingPost, body_html: v })} minHeight={200} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPost(null)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={saveEditedPost} data-testid="save-post-edit-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
